import React from 'react';
import OrderStatusProgress from 'components/order-status-progress';
import { ENUM_ORDER_PAYMENT_TYPE, ENUM_ORDER_PROGRESS } from 'utils/enum';
import { OrderType } from 'models/order.model';
import { WalletIcon } from 'cokitchen-icons';
import { ReactComponent as PaystackIcon } from 'assets/icons/paystack-icon.svg';
import { ReactComponent as BaniIcon } from 'assets/icons/bani-icon.svg';
import { ReactComponent as QuestionIcon } from 'assets/icons/question-icon-filled.svg';
import { formatCurrency } from 'utils/functions';
import { Button } from '@cokitchen/cokitchen-components';
import useDashboardContext from 'contexts/dashboard.context';

type Props = {
  step: ENUM_ORDER_PROGRESS;
  order: OrderType;
  onViewTimeline: (step?: ENUM_ORDER_PROGRESS) => void;
};

const PaymentReceived = (props: Props) => {
  const { step, order, onViewTimeline } = props;
  const { monthlyStat } = useDashboardContext();

  const subTotal =
    Number(order.calculated_order?.total_amount) -
    Number(order.calculated_order?.delivery_fee) -
    Number(order.calculated_order?.service_charge);

  return (
    <div className='relative'>
      <OrderStatusProgress step={step} order={order} onViewTimeline={onViewTimeline} />
      <div className='flex flex-col gap-[26px] p-6 border-y-4 border-[#F7F7F7]'>
        <p className='font-medium'>Select payment option</p>
        <ul className='relative flex flex-col gap-[18px]'>
          <li className='flex items-start gap-3'>
            <WalletIcon className='mt-1' />
            <article>
              <p className='text-sm font-medium'>Pay via wallet</p>
              <p className='text-sm text-[#6F7376]'>
                Balance: NGN&nbsp;
                {formatCurrency(
                  monthlyStat.business_balance,
                  {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                    style: 'decimal'
                  },
                  true
                )}
              </p>
            </article>
            {order.order_type.name === ENUM_ORDER_PAYMENT_TYPE.WALLET ? (
              <span className='self-center w-[14px] h-[14px] rounded-full border-[3.8px] border-[#E60A2B] ml-auto' />
            ) : null}
          </li>
          <li className='flex items-start gap-3'>
            <PaystackIcon className='mt-1' />
            <article>
              <p className='text-sm font-medium'>Pay with Paystack</p>
              <p className='text-sm text-[#6F7376]'>****4132</p>
            </article>
            {order.order_type.name === ENUM_ORDER_PAYMENT_TYPE.CARD ? (
              <span className='self-center w-[14px] h-[14px] rounded-full border-[3.8px] border-[#E60A2B] ml-auto' />
            ) : null}
          </li>
          <li className='flex items-start gap-3'>
            <BaniIcon className='mt-1' />
            <article>
              <p className='text-sm font-medium'>Pay via transfer</p>
              <p className='text-sm text-[#6F7376]'>Powered by Bani</p>
            </article>
            {order.order_type.name === ENUM_ORDER_PAYMENT_TYPE.CASH ? (
              <span className='self-center w-[14px] h-[14px] rounded-full border-[3.8px] border-[#E60A2B] ml-auto' />
            ) : null}
          </li>
          <div className='absolute top-0 right-0 bottom-0 left-0 bg-white/80' />
        </ul>
      </div>
      <div className='px-6 py-6'>
        <ul className='flex flex-col gap-3 pb-6 border-b-[0.8px] border-[#EAEBEB]'>
          <li className='flex items-center justify-between gap-3'>
            <p className='text-sm text-[#6F7376]'>Subtotal</p>
            <p className='text-sm text-[#6F7376] font-medium'>{formatCurrency(+subTotal)}</p>
          </li>
          <li className='flex items-center justify-between gap-3'>
            <p className='text-sm text-[#6F7376]'>Delivery fee</p>
            <p className='text-sm text-[#6F7376] font-medium'>
              {formatCurrency(+order.calculated_order?.delivery_fee)}
            </p>
          </li>
          <li className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-1'>
              <p className='text-sm text-[#6F7376]'>Service fee</p>
              <QuestionIcon className='question-icon' />
            </div>
            <p className='text-sm text-[#6F7376] font-medium'>
              {formatCurrency(+order.calculated_order?.service_charge)}
            </p>
          </li>
        </ul>
      </div>
      <div className='sticky bottom-6 p-1 bg-[#F2F2F250] rounded-bl-lg rounded-br-lg'>
        <div className='p-6 bg-white shadow-lg rounded-bl-lg rounded-br-lg'>
          <Button
            disabled
            variant='block'
            color='primary'
            className='max-w-full h-[53px] disabled:bg-[#F2F2F2] disabled:text-[#C8C9CB] disabled:border-none'>
            Complete Payment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentReceived;
